import React from "react";
import { BookingForm } from "../components/BookingForm";
import { useApp } from "../context/AppContext";
import {
  ShieldCheck,
  Wrench,
  Clock,
  Home,
  BadgeCheck,
  Laptop,
  Cpu,
  Battery,
  MonitorSmartphone,
  HardDrive,
  Zap,
  Star,
  Phone,
} from "lucide-react";

const brands = ["HP", "Dell", "Lenovo", "Apple", "Asus", "Acer", "MSI", "Samsung"];

const steps = [
  {
    n: 1,
    title: "Book online in 60 seconds",
    desc: "Tell us your laptop's brand, model, and what's wrong. Verify your mobile with OTP.",
    icon: Laptop,
  },
  {
    n: 2,
    title: "Technician reaches your doorstep",
    desc: "A verified expert arrives at your address. Share the 4-digit gate code to authorize entry.",
    icon: Home,
  },
  {
    n: 3,
    title: "Repair happens in front of you",
    desc: "Diagnosis, part replacement, and testing — done at your table. Nothing leaves your home.",
    icon: Wrench,
  },
  {
    n: 4,
    title: "Pay only when it works",
    desc: "Verify the laptop is fully repaired, share your handover code, and receive a digital invoice.",
    icon: BadgeCheck,
  },
];

const services = [
  { icon: MonitorSmartphone, title: "Screen replacement", price: "from ₹2,499", desc: "OEM-grade panels for all major laptop brands with hinge & bezel restoration." },
  { icon: Battery, title: "Battery replacement", price: "from ₹1,999", desc: "Genuine-cell battery packs with 6-month warranty and free health test." },
  { icon: Cpu, title: "Motherboard repair", price: "diagnosis ₹150", desc: "Chip-level rework, liquid-damage recovery, and short-circuit tracing." },
  { icon: HardDrive, title: "SSD / RAM upgrade", price: "from ₹1,499", desc: "Faster boot, more headroom — same-visit installation and cloning." },
  { icon: Zap, title: "Charging port fix", price: "from ₹999", desc: "DC-jack replacement, cable diagnosis, and power delivery testing." },
  { icon: Wrench, title: "OS reinstall & tune-up", price: "₹799", desc: "Fresh Windows / macOS install with drivers, data backup, and speed pass." },
];

const testimonials = [
  {
    n: "Priya M.",
    city: "Ranchi",
    text: "The technician came in an hour, replaced my ThinkPad's keyboard right on my desk, and I was back to work by lunch. Way easier than dropping it at a service center.",
  },
  {
    n: "Rohit S.",
    city: "Lalpur",
    text: "Transparent pricing — the ₹150 visit fee was upfront and there were no surprise charges. The invoice was itemized to the last screw.",
  },
  {
    n: "Anushka R.",
    city: "Kantatoli",
    text: "My lappy wouldn't turn on. Their guy diagnosed a bad charger in 10 minutes, saved me a ₹15k logic-board swap at Apple.",
  },
];

export const LandingPage = ({ onLoginClick }) => {
  const { session } = useApp();

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 pt-16 md:pt-20 pb-16">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <div className="chip chip-brand mb-6">
                <span className="dot dot-brand pulse" />
                Trusted by 100+ homes across India
              </div>
              <h1 className="font-display text-[44px] sm:text-[56px] lg:text-[68px] leading-[1.03] text-[var(--ink-1)] tracking-tight">
                Laptop broken?<br />
                We come to <span className="swash">your doorstep.</span>
              </h1>
              <p className="text-[var(--ink-2)] mt-6 text-lg leading-relaxed max-w-xl">
                MrLappy sends OEM-certified technicians to your home for on-the-spot diagnosis and
                repair. No shop visits, no lost data, no surprise bills.
              </p>

              <div className="mt-8 flex flex-wrap items-center gap-3">
                <a href="#book" className="btn-primary" data-testid="hero-book-cta">
                  Book a repair
                </a>
                <a href="#how" className="btn-secondary" data-testid="hero-how-cta">
                  How it works
                </a>
              </div>

              <div className="mt-10 flex flex-wrap items-center gap-6">
                <div className="flex items-center gap-2">
                  <div className="flex -space-x-1">
                    {[1, 2, 3, 4].map((i) => (
                      <div
                        key={i}
                        className="w-8 h-8 rounded-full border-2 border-[var(--bg)] bg-gradient-to-br from-orange-200 to-orange-400"
                        style={{
                          background: `linear-gradient(135deg, hsl(${(i * 60) % 360},70%,75%), hsl(${(i * 60 + 40) % 360},70%,60%))`,
                        }}
                      />
                    ))}
                  </div>
                  <div>
                    <div className="flex items-center gap-1 text-[var(--ink-1)]">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <Star key={i} size={14} className="fill-amber-400 text-amber-400" />
                      ))}
                      <span className="text-sm font-semibold ml-1">4.0</span>
                    </div>
                    <div className="text-xs text-[var(--ink-3)]">on 200+ verified reviews</div>
                  </div>
                </div>
                <div className="h-8 w-px bg-[var(--line-strong)] hidden sm:block" />
                <div className="flex items-center gap-2 text-sm text-[var(--ink-2)]">
                  
                </div>
              </div>
            </div>

            {/* Right visual — transparent pricing snapshot */}
            <div className="lg:col-span-5">
              <div className="relative">
                <div className="absolute -top-4 -right-4 w-24 h-24 rounded-full bg-[var(--brand-soft)] blur-2xl opacity-70" />
                <div className="card p-6 relative">
                  <div className="flex items-center justify-between mb-5">
                    <div>
                      <div className="text-xs uppercase tracking-wider text-[var(--ink-3)] font-semibold">
                        Transparent pricing
                      </div>
                      <div className="font-display text-xl text-[var(--ink-1)] mt-1">
                        No hidden charges. Ever.
                      </div>
                    </div>
                    <div className="chip chip-success">
                      <ShieldCheck size={12} /> Fixed
                    </div>
                  </div>

                  <div className="divide-y divide-[var(--line)]">
                    <div className="flex items-center justify-between py-3">
                      <div className="flex items-center gap-3">
                        <Home size={16} className="text-[var(--brand)]" />
                        <div>
                          <div className="text-sm font-semibold text-[var(--ink-1)]">
                            Doorstep visit
                          </div>
                          <div className="text-xs text-[var(--ink-3)]">One-time, fixed</div>
                        </div>
                      </div>
                      <div className="text-lg font-semibold text-[var(--ink-1)]">₹150</div>
                    </div>
                    <div className="flex items-center justify-between py-3">
                      <div className="flex items-center gap-3">
                        <MonitorSmartphone size={16} className="text-[var(--brand)]" />
                        <div>
                          <div className="text-sm font-semibold text-[var(--ink-1)]">
                            Screen replacement
                          </div>
                          <div className="text-xs text-[var(--ink-3)]">OEM-grade panels</div>
                        </div>
                      </div>
                      <div className="text-sm font-semibold text-[var(--ink-2)]">from ₹2,499</div>
                    </div>
                    <div className="flex items-center justify-between py-3">
                      <div className="flex items-center gap-3">
                        <Battery size={16} className="text-[var(--brand)]" />
                        <div>
                          <div className="text-sm font-semibold text-[var(--ink-1)]">
                            Battery replacement
                          </div>
                          <div className="text-xs text-[var(--ink-3)]">6-month warranty</div>
                        </div>
                      </div>
                      <div className="text-sm font-semibold text-[var(--ink-2)]">from ₹1,999</div>
                    </div>
                    <div className="flex items-center justify-between py-3">
                      <div className="flex items-center gap-3">
                        <HardDrive size={16} className="text-[var(--brand)]" />
                        <div>
                          <div className="text-sm font-semibold text-[var(--ink-1)]">
                            SSD / RAM upgrade
                          </div>
                          <div className="text-xs text-[var(--ink-3)]">Same-visit install</div>
                        </div>
                      </div>
                      <div className="text-sm font-semibold text-[var(--ink-2)]">from ₹1,499</div>
                    </div>
                  </div>

                  <a
                    href="#book"
                    className="mt-5 flex items-center justify-center gap-2 text-sm font-semibold text-[var(--brand-deep)] hover:text-[var(--brand)]"
                    data-testid="pricing-see-full-cta"
                  >
                    Book your repair now →
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Brand strip */}
        <div className="border-y border-[var(--line)] bg-[var(--bg-soft)]/70">
          <div className="max-w-7xl mx-auto px-6 py-6">
            <div className="text-xs uppercase tracking-wider text-[var(--ink-3)] text-center mb-4">
              We repair every major laptop brand
            </div>
            <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4 text-[var(--ink-2)]">
              {brands.map((b) => (
                <span
                  key={b}
                  className="font-display text-xl md:text-2xl text-[var(--ink-2)] opacity-80"
                >
                  {b}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="max-w-7xl mx-auto px-6 py-20">
        <div className="max-w-2xl mb-12">
          <div className="eyebrow mb-3">How it works</div>
          <h2 className="font-display text-4xl md:text-5xl text-[var(--ink-1)] leading-tight">
            From booking to repair in <span className="font-serif-italic text-[var(--brand)]">under an hour.</span>
          </h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {steps.map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.n} className="card p-6 relative hover:-translate-y-0.5 transition-transform">
                <div className="flex items-center justify-between mb-6">
                  <span className="step-num">{s.n}</span>
                  <Icon size={20} className="text-[var(--brand)]" />
                </div>
                <h3 className="font-display text-xl text-[var(--ink-1)] leading-snug">
                  {s.title}
                </h3>
                <p className="text-sm text-[var(--ink-2)] mt-2 leading-relaxed">{s.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* SERVICES */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <div className="max-w-2xl">
            <div className="eyebrow mb-3">What we fix</div>
            <h2 className="font-display text-4xl md:text-5xl text-[var(--ink-1)] leading-tight">
              Every repair, <span className="font-serif-italic text-[var(--brand)]">at your table.</span>
            </h2>
          </div>
          <div className="text-sm text-[var(--ink-2)] max-w-sm">
            Transparent pricing. Genuine parts. 
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {services.map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.title} className="card p-6 hover:border-[var(--brand)] transition-colors">
                <div className="w-11 h-11 rounded-xl bg-[var(--brand-soft)] flex items-center justify-center">
                  <Icon size={18} className="text-[var(--brand-deep)]" />
                </div>
                <div className="flex items-baseline justify-between mt-5">
                  <h4 className="font-display text-xl text-[var(--ink-1)]">{s.title}</h4>
                  <span className="text-sm font-semibold text-[var(--brand-deep)]">{s.price}</span>
                </div>
                <p className="text-sm text-[var(--ink-2)] mt-2 leading-relaxed">{s.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="max-w-2xl mb-12">
          <div className="eyebrow mb-3">What customers say</div>
          <h2 className="font-display text-4xl md:text-5xl text-[var(--ink-1)] leading-tight">
            Real reviews. <span className="font-serif-italic text-[var(--brand)]">Real relief.</span>
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {testimonials.map((t) => (
            <div key={t.n} className="card p-6">
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star key={i} size={14} className="fill-amber-400 text-amber-400" />
                ))}
              </div>
              <p className="text-[15px] text-[var(--ink-1)] leading-relaxed mt-4">"{t.text}"</p>
              <div className="mt-5 flex items-center gap-3">
                <div
                  className="w-9 h-9 rounded-full"
                  style={{
                    background: `linear-gradient(135deg, hsl(${(t.n.charCodeAt(0) * 7) % 360},70%,75%), hsl(${(t.n.charCodeAt(0) * 11) % 360},70%,55%))`,
                  }}
                />
                <div>
                  <div className="text-sm font-semibold text-[var(--ink-1)]">{t.n}</div>
                  <div className="text-xs text-[var(--ink-3)]">{t.city}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* BOOKING */}
      <section id="book" className="max-w-5xl mx-auto px-6 py-20">
        <div className="max-w-2xl mb-10 mx-auto text-center">
          <div className="eyebrow mb-3 justify-center inline-flex">Book your repair</div>
          <h2 className="font-display text-4xl md:text-5xl text-[var(--ink-1)] leading-tight">
            Tell us what's wrong <span className="font-serif-italic text-[var(--brand)]">— we'll handle the rest.</span>
          </h2>
        </div>
        <BookingForm onSubmitted={({ needAuth }) => needAuth && onLoginClick?.()} />
        {!session && (
          <div className="mt-4 text-sm text-center text-[var(--ink-3)]">
            You'll be prompted to verify your mobile number before your booking is confirmed.
          </div>
        )}
      </section>

      {/* FOOTER */}
      <footer className="border-t border-[var(--line)] bg-[var(--bg-soft)]">
        <div className="max-w-7xl mx-auto px-6 py-12 grid md:grid-cols-4 gap-8">
          <div className="md:col-span-2">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-[var(--brand)] flex items-center justify-center">
                <Laptop size={16} className="text-white" />
              </div>
              <div className="font-display text-xl text-[var(--ink-1)]">
                MrLappy<span className="text-[var(--brand)]">.</span>
              </div>
            </div>
            <p className="text-sm text-[var(--ink-2)] mt-3 leading-relaxed max-w-sm">
              India's doorstep laptop repair service. OEM-certified technicians, transparent pricing.
              
            </p>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-[var(--ink-3)] font-semibold">Contact</div>
            <div className="mt-4 space-y-2 text-sm text-[var(--ink-1)]">
              <div className="flex items-center gap-2">
                <Phone size={13} className="text-[var(--brand)]" /> +91 9709806537
              </div>
              <div>mrlappyservice@gmail.com</div>
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-[var(--ink-3)] font-semibold">Cities</div>
            <div className="mt-4 text-sm text-[var(--ink-2)] leading-relaxed">
              Ranchi
            </div>
          </div>
        </div>
        <div className="border-t border-[var(--line)]">
          <div className="max-w-7xl mx-auto px-6 py-5 flex flex-wrap items-center justify-between gap-3 text-xs text-[var(--ink-3)]">
            <div>© {new Date().getFullYear()} MrLappy Logistics Pvt. Ltd. · Made in India</div>
            <div>Terms · Privacy · Refunds</div>
          </div>
        </div>
      </footer>
    </div>
  );
};
